<script setup>
import { BuildingOffice2Icon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import BubbleLine from "@js/components/chats/BubbleLine.vue";
import InputField from "@js/components/fields/InputField.vue";
import TextareaField from "@js/components/fields/TextareaField.vue";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { nextTick, reactive, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";
import { mixed, object, string } from "yup";

const { t } = useI18n();

const { route } = useRoute();

const {
  tenantTask,
  callback,
  defaultOpen = false,
} = defineProps({
  tenantTask: {
    type: Object,
    required: true,
  },
  callback: {
    type: Function,
    required: false,
  },
  defaultOpen: {
    type: Boolean,
    required: false,
  },
});

const innerTenantTask = toRef(() => tenantTask);

const loading = ref(false);

const chat = ref(null);

const isOpen = ref(defaultOpen);

const hasChanges = ref(false);

const form = reactive({
  message: "",
  attachments: null,
});

const validationSchema = object({
  message: string().required(t("error.required")),
  attachments: mixed().nullable(),
});

function setIsOpen(value) {
  isOpen.value = value;

  if (!value && callback && hasChanges.value) {
    hasChanges.value = false;
    callback();
  }

  if (value) {
    scrollToBottom();
  }
}

function saveAction() {
  loading.value = true;

  let data = new FormData();
  data.append("message", form.message);

  if (form.attachments) {
    let attachments = document.getElementById("attachments");

    for (let i = 0; i < attachments.files.length; i++) {
      data.append("attachments[]", attachments.files[i]);
    }
  }

  fetch(
    route("front.projectManagement.task.postCustomerTaskAnswer", {
      tenantTask: innerTenantTask.value.id,
    }),
    {
      method: "POST",
      headers: {
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: data,
    },
  )
    .then((response) => response.json())
    .then((data) => {
      hasChanges.value = true;
      innerTenantTask.value.orderedAnswers = data.tenantTask.orderedAnswers;
      form.message = "";
      form.attachments = null;
      document.getElementById("attachments").value = "";
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      loading.value = false;
      scrollToBottom();
    });
}

function scrollToBottom() {
  nextTick(() => {
    chat.value.scrollIntoView({ behavior: "smooth", block: "end" });
  });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen" size="extra-large">
    <template #title>
      {{ t("title.chat") }}
    </template>

    <template #content>
      <LoadingSpinner v-if="loading" />

      <div v-else ref="chat" class="flex flex-col gap-4">
        <BubbleLine
          v-for="(orderAnswer, index) in innerTenantTask.orderedAnswers"
          :key="index"
          :name="orderAnswer.name"
          :message="orderAnswer.message"
          :date="formatDate(orderAnswer.createdAt, false, true)"
          :attachments="orderAnswer.attachments"
          :right="!orderAnswer.isCustomer"
          :icon="!orderAnswer.isCustomer ? BuildingOffice2Icon : null"
        />
      </div>
    </template>

    <template #footer>
      <Form
        :validation-schema="validationSchema"
        @submit="saveAction"
        v-slot="{ errors }"
      >
        <TextareaField
          v-model="form.message"
          field-name="message"
          field-id="message"
          class="h-28 w-full"
          resize="none"
        />

        <InputField
          v-model="form.attachments"
          :placeholder="t('label.attachments')"
          field-name="attachments"
          field-id="attachments"
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          multiple
        />

        <ActionButton
          submit
          :disabled="Object.keys(errors).length > 0 || loading"
          :loading="loading"
          class="mt-auto"
        >
          {{ t("button.send") }}
        </ActionButton>
      </Form>
    </template>
  </Drawer>
</template>
